import type { OnboardingTargetId } from "../onboarding/onboardingTargets";
import { Tooltip } from "@mui/material";
import classNames from "classnames";
import { MouseEvent, ReactNode } from "react";

export interface EditorRailButtonProps {
  label: string;
  icon: JSX.Element;
  active?: boolean;
  danger?: boolean;
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  href?: string;
  externalLink?: boolean;
  id?: string;
  testId?: string;
  onboardingId?: OnboardingTargetId;
}

export function EditorRailButton(props: EditorRailButtonProps) {
  const {
    label,
    icon,
    active,
    danger,
    disabled,
    onClick,
    href,
    externalLink,
    id,
    testId,
    onboardingId,
  } = props;

  const className = classNames(
    "w-10 h-10 flex items-center justify-center rounded-5 cursor-pointer box-border text-[20px] transition-colors",
    active
      ? "bg-creator-primary-washed text-creator-primary"
      : danger
      ? "text-creator-error hover:bg-creator-error-washed"
      : "text-creator-text-sub hover:bg-creator-childHover hover:text-creator-text",
    disabled && "opacity-45 cursor-not-allowed pointer-events-none"
  );

  const content = href && !disabled ? (
    <a
      href={href}
      target={externalLink ? "_blank" : undefined}
      rel={externalLink ? "noreferrer" : undefined}
      id={id}
      aria-label={label}
      data-testid={testId}
      data-onboarding-id={onboardingId}
      data-active={active ? "true" : undefined}
      data-external={externalLink ? "true" : undefined}
      data-link={href}
      className={className}
    >
      {icon}
    </a>
  ) : (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      id={id}
      data-testid={testId}
      data-onboarding-id={onboardingId}
      data-active={active ? "true" : undefined}
      className={className}
    >
      {icon}
    </button>
  );

  return (
    <Tooltip title={label} placement="right" arrow>
      <span className="inline-flex">{content}</span>
    </Tooltip>
  );
}

export function EditorRailDivider(props: { testId?: string }) {
  return (
    <div
      className="w-6 h-px bg-creator-border my-1"
      data-testid={props.testId}
    />
  );
}

export default function EditorRail(props: {
  children: ReactNode;
  bottom?: ReactNode;
  className?: string;
  testId: string;
  onboardingId?: OnboardingTargetId;
}) {
  const { children, bottom, className, testId, onboardingId } = props;

  return (
    <div
      className={classNames(
        "w-12 min-w-12 h-full flex flex-col items-center gap-1.5 py-2 bg-creator-primary-background border-r border-creator-border",
        className
      )}
      data-testid={testId}
      data-onboarding-id={onboardingId}
    >
      {children}

      {bottom && (
        <div className="mt-auto flex flex-col items-center gap-1.5">
          {bottom}
        </div>
      )}
    </div>
  );
}
